import React from 'react';
import {
  Layout,
  Menu,
  Icon,
  Card,
  Row,
  Col,
  Button,
  Tag,
  Divider,
  Alert,
} from 'antd';

import '../../assets/css/App.css';
import Header1 from '../public';
import PrimeImg from "../../assets/images/clarityICI.jpg";

const { Content, Sider } = Layout;
const SubMenu = Menu.SubMenu;

/* ============================================================
   PRIME-ICI data
   ============================================================ */

const MODEL_LABEL = "nsNMF-3 NMF";

const PHENOTYPE_COLORS = {
  molecular: "#7D83FF",
  immunogenic: "#F46BD7",
  metastatic: "#48C9B0",
};

const cohortDistribution = [
  {
    cohort: "Training",
    molecular: 49.3,
    immunogenic: 26.2,
    metastatic: 24.5,
  },
  {
    cohort: "East Asian validation",
    molecular: 48.2,
    immunogenic: 20.4,
    metastatic: 31.4,
  },
  {
    cohort: "Western validation",
    molecular: 60.0,
    immunogenic: 17.1,
    metastatic: 22.9,
  },
];

const validationCardStyle = {
  borderRadius: 18,
  boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
  minHeight: 560,
  height: "100%",
};

const validationSectionTitleStyle = {
  color: "#6B5BFF",
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: "uppercase",
  fontSize: 13,
  marginBottom: 8,
};

const RADAR_AXES = [
  { key: "ros1", label: "ROS1" },
  { key: "treatmentLine", label: "Treatment line" },
  { key: "liverMetastasis", label: "Liver metastasis" },
  { key: "brainMetastasis", label: "Brain metastasis" },
  { key: "boneMetastasis", label: "Bone metastasis" },
  { key: "tumorPurity", label: "Tumor purity" },
  { key: "tumorDiameter", label: "Tumor diameter" },
  { key: "tmb", label: "TMB" },
  { key: "nlr", label: "NLR" },
  { key: "met", label: "MET" },
  { key: "egfr", label: "EGFR" },
  { key: "kras", label: "KRAS" },
];

const phenotypeData = {
  molecular: {
    key: "molecular",
    short: "Phenotype 1",
    name: "Molecularly driven",
    color: PHENOTYPE_COLORS.molecular,
    icon: "deployment-unit",
    survival: "Intermediate overall survival",
    subtitle: "Oncogenic driver dependence and molecularly defined tumor state",
    description:
      "This phenotype is mainly shaped by ROS1, KRAS, EGFR, and MET alterations, together with PD-L1 expression and HLA loss of heterozygosity. It reflects a tumor-intrinsic molecular program associated with intermediate survival outcomes.",
    drivers: [
      { feature: "ROS1 alteration", weight: 0.0719 },
      { feature: "KRAS mutation", weight: 0.0689 },
      { feature: "EGFR mutation", weight: 0.0601 },
      { feature: "HLA LOH", weight: 0.0498 },
      { feature: "PD-L1 expression", weight: 0.0497 },
      { feature: "Albumin", weight: 0.0481 },
      { feature: "ECOG PS", weight: 0.0437 },
      { feature: "Age ≥65 years", weight: 0.0409 },
      { feature: "Smoking history", weight: 0.0366 },
      { feature: "MET alteration", weight: 0.0351 },
    ],
    radar: {
  ros1: 100,
  treatmentLine: 5,
  liverMetastasis: 25,
  brainMetastasis: 65,
  boneMetastasis: 10,
  tumorPurity: 20,
  tumorDiameter: 20,
  tmb: 25,
  nlr: 50,
  met: 100,
  egfr: 100,
  kras: 90,
},
  },

  immunogenic: {
    key: "immunogenic",
    short: "Phenotype 2",
    name: "Immunogenic",
    color: PHENOTYPE_COLORS.immunogenic,
    icon: "thunderbolt",
    survival: "Most favorable overall survival",
    subtitle: "Immune-active and host-response phenotype linking other two states",
    description:
      "This phenotype is characterized by TMB, NLR, tumor purity, albumin, histology, treatment regimen, prior systemic therapy, and treatment line. Low-dimensional visualization suggests that it occupies an intermediate position between the molecularly driven and metastatic-burden phenotypes.",
    drivers: [
      { feature: "Sex", weight: 0.0644 },
      { feature: "Treatment regimen", weight: 0.0637 },
      { feature: "NLR", weight: 0.0596 },
      { feature: "Histology", weight: 0.0553 },
      { feature: "Tumor diameter", weight: 0.0514 },
      { feature: "Tumor purity", weight: 0.0464 },
      { feature: "Albumin", weight: 0.0462 },
      { feature: "TMB", weight: 0.0439 },
      { feature: "Treatment line", weight: 0.0396 },
      { feature: "Prior systemic therapy", weight: 0.0395 },
    ],
    radar: {
  ros1: 12,
  treatmentLine: 55,
  liverMetastasis: 18,
  brainMetastasis: 25,
  boneMetastasis: 25,
  tumorPurity: 90,
  tumorDiameter: 100,
  tmb: 90,
  nlr: 90,
  met: 0,
  egfr: 20,
  kras: 5,
},
  },

  metastatic: {
    key: "metastatic",
    short: "Phenotype 3",
    name: "Metastatic-burden",
    color: PHENOTYPE_COLORS.metastatic,
    icon: "cluster",
    survival: "Poorest overall survival",
    subtitle:
      "Metastatic dissemination, prior treatment exposure, and genomic instability",
    description:
      "This phenotype is dominated by bone, brain, and liver metastases, prior systemic therapy, other genomic alterations, FGA, HLA LOH, tumor purity, and mutation count. It reflects an advanced systemic disease-burden state.",
    drivers: [
      { feature: "Bone metastasis", weight: 0.0676 },
      { feature: "Brain metastasis", weight: 0.0612 },
      { feature: "Prior systemic therapy", weight: 0.0608 },
      { feature: "Treatment line", weight: 0.0607 },
      { feature: "Liver metastasis", weight: 0.0552 },
      { feature: "Other genomic alteration", weight: 0.0522 },
      { feature: "FGA", weight: 0.0472 },
      { feature: "HLA LOH", weight: 0.0434 },
      { feature: "Tumor purity", weight: 0.0376 },
      { feature: "Mutation count", weight: 0.0367 },
    ],
    radar: {
  ros1: 5,
  treatmentLine: 95,
  liverMetastasis: 95,
  brainMetastasis: 100,
  boneMetastasis: 95,
  tumorPurity: 75,
  tumorDiameter: 65,
  tmb: 55,
  nlr: 5,
  met: 0,
  egfr: 45,
  kras: 10,
},
  },
};

const matcherFeatures = [
  {
    key: "driver",
    label: "EGFR / ROS1 / MET driver alteration",
  },
  {
    key: "kras",
    label: "KRAS mutation or smoking-associated biology",
  },
  {
    key: "pdl1",
    label: "PD-L1 positive/high",
  },
  {
    key: "tmb",
    label: "TMB-high",
  },
  {
    key: "nlr",
    label: "High NLR / inflammatory status",
  },
  {
    key: "albumin",
    label: "Favorable albumin / nutritional reserve",
  },
  {
    key: "brain",
    label: "Brain metastasis",
  },
  {
    key: "bone",
    label: "Bone metastasis",
  },
  {
    key: "liver",
    label: "Liver metastasis",
  },
  {
    key: "laterLine",
    label: "Second-line or later ICI",
  },
  {
    key: "fga",
    label: "High FGA / genomic instability",
  },
];

/* ============================================================
   Utility functions
   ============================================================ */

function scorePattern(flags) {
  const scores = {
    molecular: 0,
    immunogenic: 0,
    metastatic: 0,
  };

  if (flags.driver) scores.molecular += 4;
  if (flags.kras) scores.molecular += 2;

  if (flags.pdl1) {
    scores.molecular += 1;
    scores.immunogenic += 2;
  }

  if (flags.tmb) scores.immunogenic += 4;
  if (flags.nlr) scores.immunogenic += 2;
  if (flags.albumin) scores.immunogenic += 2;

  if (flags.brain) scores.metastatic += 4;
  if (flags.bone) scores.metastatic += 4;
  if (flags.liver) scores.metastatic += 4;
  if (flags.laterLine) scores.metastatic += 3;
  if (flags.fga) scores.metastatic += 3;

  const ranked = Object.keys(scores).sort(function(a, b) {
    return scores[b] - scores[a];
  });

  return {
    scores,
    best: ranked[0],
  };
}

function percentText(value) {
  return value.toFixed(1) + "%";
}

function maxDriverWeight(drivers) {
  let maxValue = 0;

  drivers.forEach(function(item) {
    if (item.weight > maxValue) {
      maxValue = item.weight;
    }
  });

  return maxValue;
}

/* ============================================================
   PRIME-ICI React component
   ============================================================ */

class PRIMEICI extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedPhenotype: "immunogenic",
      matcherFlags: {},
      selectedMenu: "introduction",
    };
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleWindowScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleWindowScroll);
  }

  selectPhenotype = (key) => {
    this.setState({
      selectedPhenotype: key,
    });
  };

  scrollToSection = (sectionId, menuKey) => {
    const element = document.getElementById(sectionId);

    if (!element) {
      return;
    }

    const offset = 90;
    const top =
      element.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    this.setState({
      selectedMenu: menuKey,
    });
  };

  handleMenuClick = ({ key }) => {
    const sectionMap = {
      introduction: "prime-introduction",
      phenotypes: "prime-phenotypes",
      validation: "prime-validation",
      matcher: "prime-matcher",
      about: "prime-about",
    };

    if (!sectionMap[key]) {
      return;
    }

    this.scrollToSection(sectionMap[key], key);
  };

  handleWindowScroll = () => {
    const sections = [
      { key: "introduction", id: "prime-introduction" },
      { key: "phenotypes", id: "prime-phenotypes" },
      { key: "validation", id: "prime-validation" },
      { key: "matcher", id: "prime-matcher" },
      { key: "about", id: "prime-about" },
    ];

    let currentKey = this.state.selectedMenu;

    sections.forEach(function(section) {
      const element = document.getElementById(section.id);

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();

      if (rect.top <= 140) {
        currentKey = section.key;
      }
    });

    if (currentKey !== this.state.selectedMenu) {
      this.setState({
        selectedMenu: currentKey,
      });
    }
  };

  toggleMatcherFeature = (key) => {
    const currentFlags = Object.assign({}, this.state.matcherFlags);

    currentFlags[key] = !currentFlags[key];

    this.setState({
      matcherFlags: currentFlags,
    });
  };

  renderHero() {
    return (
      <div
        style={{
          background: `linear-gradient(90deg, rgba(5, 8, 20, 0.88), rgba(20, 18, 46, 0.65)), url(${PrimeImg}) no-repeat center center`,
          height: 360,
          width: "100%",
          backgroundSize: "cover",
          borderRadius: 18,
          overflow: "hidden",
          boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
        }}
      >
        <div
          style={{
            color: "#fff",
            padding: "58px 70px",
          }}
        >
          <Tag
            color="purple"
            style={{
              fontSize: 18,
              padding: "6px 12px",
              borderRadius: 20,
              marginBottom: 22,
            }}
          >
            PRIME-ICI · {MODEL_LABEL} · 8,052 patients · 44 International cohorts
          </Tag>

          <div
            style={{
              fontSize: 44,
              lineHeight: 1.12,
              fontWeight: 800,
              maxWidth: 980,
              letterSpacing: -1,
            }}
          >
            Unsupervised Clinical–Molecular–Immune Phenotyping of NSCLC
            Immunotherapy Outcomes
          </div>

          <div
            style={{
              fontSize: 19,
              lineHeight: 1.65,
              maxWidth: 880,
              color: "#DDE6FF",
              marginTop: 20,
            }}
          >
            PRIME-ICI identifies three reproducible biological states shaped by
            molecular driver dependence, tumor immunogenicity, and metastatic
            disease burden.
          </div>

          <div style={{ marginTop: 28 }}>
            <Button
              type="primary"
              size="large"
              onClick={() =>
                this.scrollToSection("prime-phenotypes", "phenotypes")
              }
              style={{
                marginRight: 12,
                background: "#7D83FF",
                borderColor: "#7D83FF",
              }}
            >
              Explore phenotypes
            </Button>

            <Button
              size="large"
              onClick={() => this.scrollToSection("prime-matcher", "matcher")}
            >
              Try pattern matcher
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderSummaryCards() {
    const cards = [
      {
        value: "8,052",
        label: "NSCLC patients",
        icon: "team",
        color: "#7D83FF",
      },
      {
        value: "44",
        label: "International cohorts",
        icon: "global",
        color: "#F46BD7",
      },
      {
        value: "4,983",
        label: "Training dataset",
        icon: "database",
        color: "#48C9B0",
      },
      {
        value: "3,069",
        label: "External validation",
        icon: "check-circle",
        color: "#FFB84D",
      },
      {
        value: "0.08",
        label: "Cramer’s V",
        icon: "bar-chart",
        color: "#91D5FF",
      },
      {
        value: ">0.9",
        label: "Cosine similarity",
        icon: "radar-chart",
        color: "#B37FEB",
      },
    ];

    return (
      <Row gutter={16} style={{ marginTop: 24 }}>
        {cards.map(function(item) {
          return (
            <Col span={4} key={item.label}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  minHeight: 142,
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%)",
                  boxShadow: "0 12px 32px rgba(25, 35, 80, 0.08)",
                }}
              >
                <Icon
                  type={item.icon}
                  style={{
                    fontSize: 26,
                    color: item.color,
                    marginBottom: 10,
                  }}
                />

                <div
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#1F2A44",
                  }}
                >
                  {item.value}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: "#667085",
                    marginTop: 4,
                  }}
                >
                  {item.label}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  }

  renderAxisCards() {
    const axisCards = [
      {
        title: "Molecular driver dependence",
        text:
          "ROS1, KRAS, EGFR, MET, PD-L1 expression, and HLA LOH define the molecularly driven state.",
        icon: "deployment-unit",
        color: PHENOTYPE_COLORS.molecular,
      },
      {
        title: "Tumor immunogenicity",
        text:
          "TMB, tumor purity, NLR, albumin, histology, and treatment context define the immunogenic state.",
        icon: "thunderbolt",
        color: PHENOTYPE_COLORS.immunogenic,
      },
      {
        title: "Metastatic disease burden",
        text:
          "Bone, brain, and liver metastases, prior therapy, FGA, and mutation count define the metastatic-burden state.",
        icon: "cluster",
        color: PHENOTYPE_COLORS.metastatic,
      },
    ];

    return (
      <Card
        bordered={false}
        style={{
          marginTop: 24,
          borderRadius: 18,
          boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              color: "#6B5BFF",
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: "uppercase",
              fontSize: 13,
            }}
          >
            Core finding
          </div>

          <div
            style={{
              fontSize: 30,
              fontWeight: 800,
              color: "#1F2A44",
              marginTop: 6,
            }}
          >
            Three biological axes structure NSCLC immunotherapy outcomes
          </div>

          <div
            style={{
              fontSize: 16,
              color: "#667085",
              marginTop: 8,
              maxWidth: 900,
            }}
          >
            PRIME-ICI moves from cluster discovery to a biological architecture
            of overall survival outcomes.
          </div>
        </div>

        <Row gutter={18}>
          {axisCards.map(function(item) {
            return (
              <Col span={8} key={item.title}>
                <div
                  style={{
                    border: "1px solid #EEF1F7",
                    borderRadius: 16,
                    padding: 22,
                    minHeight: 210,
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
                  }}
                >
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 16,
                      background: item.color + "18",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 16,
                    }}
                  >
                    <Icon
                      type={item.icon}
                      style={{
                        fontSize: 26,
                        color: item.color,
                      }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: "#1F2A44",
                      marginBottom: 10,
                    }}
                  >
                    {item.title}
                  </div>

                  <div
                    style={{
                      fontSize: 15,
                      lineHeight: 1.65,
                      color: "#667085",
                    }}
                  >
                    {item.text}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  }

  renderPhenotypeSelector() {
    const self = this;

    return (
      <div>
        {Object.keys(phenotypeData).map(function(key) {
          const item = phenotypeData[key];
          const isActive = self.state.selectedPhenotype === key;

          return (
            <div
              key={key}
              onClick={() => self.selectPhenotype(key)}
              style={{
                cursor: "pointer",
                border: "2px solid " + (isActive ? item.color : "#EEF1F7"),
                borderRadius: 16,
                padding: 16,
                marginBottom: 14,
                background: isActive ? item.color + "10" : "#ffffff",
                boxShadow: isActive
                  ? "0 12px 28px " + item.color + "22"
                  : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: item.color + "18",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 14,
                  }}
                >
                  <Icon
                    type={item.icon}
                    style={{
                      color: item.color,
                      fontSize: 24,
                    }}
                  />
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: "#1F2A44",
                    }}
                  >
                    {item.name}
                  </div>

                  <div
                    style={{
                      color: "#667085",
                      fontSize: 13,
                      marginTop: 2,
                    }}
                  >
                    {item.short}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderPhenotypeDetail() {
    const item = phenotypeData[this.state.selectedPhenotype];

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 24,
          background:
            "linear-gradient(180deg, " +
            item.color +
            "12 0%, #ffffff 100%)",
          border: "1px solid " + item.color + "55",
          minHeight: 340,
        }}
      >
        <Tag
          style={{
            background: item.color,
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "4px 12px",
            marginBottom: 14,
          }}
        >
          {item.short}
        </Tag>

        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: "#1F2A44",
            marginBottom: 8,
          }}
        >
          {item.name}
        </div>

        <div
          style={{
            fontSize: 16,
            color: "#667085",
            marginBottom: 18,
          }}
        >
          {item.subtitle}
        </div>

        <Divider />

        <div
          style={{
            fontSize: 15,
            lineHeight: 1.8,
            color: "#344054",
          }}
        >
          {item.description}
        </div>

        <div
          style={{
            marginTop: 18,
            padding: "10px 14px",
            display: "inline-block",
            borderRadius: 20,
            background: item.color + "18",
            color: item.color,
            fontWeight: 800,
          }}
        >
          <Icon type="line-chart" /> {item.survival}
        </div>
      </div>
    );
  }

  renderDriverBars() {
    const item = phenotypeData[this.state.selectedPhenotype];
    const maxWeight = maxDriverWeight(item.drivers);

    return (
      <div>
        {item.drivers.map(function(driver) {
          const width = (driver.weight / maxWeight) * 100;

          return (
            <div key={driver.feature} style={{ marginBottom: 13 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  marginBottom: 5,
                }}
              >
                <span style={{ color: "#344054", fontWeight: 600 }}>
                  {driver.feature}
                </span>
                <span style={{ color: "#667085" }}>
                  {driver.weight.toFixed(4)}
                </span>
              </div>

              <div
                style={{
                  height: 10,
                  background: "#EEF1F7",
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: 10,
                    width: width + "%",
                    background:
                      "linear-gradient(90deg, " +
                      item.color +
                      " 0%, " +
                      item.color +
                      "AA 100%)",
                    borderRadius: 10,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderDistributionBars() {
  return (
    <div>
      {cohortDistribution.map(function(row) {
        return (
          <div key={row.cohort} style={{ marginBottom: 28 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#1F2A44",
                marginBottom: 10,
              }}
            >
              {row.cohort}
            </div>

            <div
              style={{
                display: "flex",
                height: 36,
                borderRadius: 18,
                overflow: "hidden",
                background: "#EEF1F7",
              }}
            >
              <div
                style={{
                  width: row.molecular + "%",
                  background: PHENOTYPE_COLORS.molecular,
                  color: "#fff",
                  textAlign: "center",
                  lineHeight: "36px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {percentText(row.molecular)}
              </div>

              <div
                style={{
                  width: row.immunogenic + "%",
                  background: PHENOTYPE_COLORS.immunogenic,
                  color: "#fff",
                  textAlign: "center",
                  lineHeight: "36px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {percentText(row.immunogenic)}
              </div>

              <div
                style={{
                  width: row.metastatic + "%",
                  background: PHENOTYPE_COLORS.metastatic,
                  color: "#fff",
                  textAlign: "center",
                  lineHeight: "36px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {percentText(row.metastatic)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

renderSharedPhenotypeLegend() {
  const legendItems = [
    {
      label: "Molecularly driven",
      color: PHENOTYPE_COLORS.molecular,
    },
    {
      label: "Immunogenic",
      color: PHENOTYPE_COLORS.immunogenic,
    },
    {
      label: "Metastatic-burden",
      color: PHENOTYPE_COLORS.metastatic,
    },
  ];

  return (
    <div
      style={{
        marginTop: 18,
        padding: "14px 18px",
        borderRadius: 16,
        background: "#FFFFFF",
        border: "1px solid #EEF1F7",
        boxShadow: "0 8px 22px rgba(25,35,80,0.06)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 28,
      }}
    >
      {legendItems.map(function(item) {
        return (
          <div
            key={item.label}
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 15,
              fontWeight: 700,
              color: "#1F2A44",
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 4,
                background: item.color,
                display: "inline-block",
                marginRight: 8,
              }}
            />
            {item.label}
          </div>
        );
      })}
    </div>
  );
}

renderValidationStatistics() {
  const stats = [
    {
      label: "Cramer’s V",
      value: "0.08",
      note: "Small cross-cohort effect size",
      icon: "partition",
    },
    {
      label: "Spearman ρ",
      value: "0.604–0.855",
      note: "Phenotype-specific rank concordance",
      icon: "line-chart",
    },
    {
      label: "Cosine similarity",
      value: "0.897–0.962",
      note: "Preserved feature architecture",
      icon: "deployment-unit",
    },
  ];

  return (
    <div
      style={{
        marginTop: 14,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 14,
      }}
    >
      {stats.map(function(item) {
        return (
          <div
            key={item.label}
            style={{
              padding: "14px 16px",
              borderRadius: 14,
              background: "#F8FAFC",
              border: "1px solid #E4E7EC",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                color: "#667085",
                fontSize: 13,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              <Icon
                type={item.icon}
                style={{
                  marginRight: 7,
                  color: "#667085",
                }}
              />
              {item.label}
            </div>

            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#1F2A44",
                marginBottom: 4,
              }}
            >
              {item.value}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#98A2B3",
                lineHeight: 1.45,
              }}
            >
              {item.note}
            </div>
          </div>
        );
      })}
    </div>
  );
}

  renderContinuum() {
    return (
      <Card
        bordered={false}
        style={{
          marginTop: 24,
          borderRadius: 18,
          boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
        }}
      >
        <div
          style={{
            color: "#6B5BFF",
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 13,
          }}
        >
          Continuum model
        </div>

        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: "#1F2A44",
            marginTop: 6,
          }}
        >
          Immunogenic phenotype bridges molecular driver dependence and
          metastatic progression
        </div>

        <div
          style={{
            fontSize: 16,
            color: "#667085",
            marginTop: 8,
            marginBottom: 30,
            maxWidth: 900,
          }}
        >
          t-SNE visualization suggested that the immunogenic phenotype occupies
          an intermediate region between the molecularly driven and
          metastatic-burden states.
        </div>

        <div
          style={{
            position: "relative",
            height: 230,
            marginTop: 20,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 100,
              left: "10%",
              right: "10%",
              height: 10,
              borderRadius: 20,
              background:
                "linear-gradient(90deg, #7D83FF 0%, #F46BD7 50%, #48C9B0 100%)",
              boxShadow: "0 0 30px rgba(125,131,255,0.25)",
            }}
          />

          {[
            {
              left: "7%",
              top: 58,
              title: "Molecularly driven",
              text: "Oncogenic dependence",
              color: PHENOTYPE_COLORS.molecular,
              icon: "deployment-unit",
            },
            {
              left: "39%",
              top: 28,
              title: "Immunogenic",
              text: "Intermediate immune-active state",
              color: PHENOTYPE_COLORS.immunogenic,
              icon: "thunderbolt",
            },
            {
              left: "70%",
              top: 58,
              title: "Metastatic-burden",
              text: "Systemic dissemination",
              color: PHENOTYPE_COLORS.metastatic,
              icon: "cluster",
            },
          ].map(function(item) {
            return (
              <div
                key={item.title}
                style={{
                  position: "absolute",
                  left: item.left,
                  top: item.top,
                  width: 230,
                  padding: 18,
                  background: "#fff",
                  borderRadius: 18,
                  border: "2px solid " + item.color,
                  textAlign: "center",
                  boxShadow: "0 12px 32px " + item.color + "22",
                }}
              >
                <Icon
                  type={item.icon}
                  style={{
                    fontSize: 28,
                    color: item.color,
                    marginBottom: 8,
                  }}
                />

                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: "#1F2A44",
                  }}
                >
                  {item.title}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: "#667085",
                    marginTop: 4,
                  }}
                >
                  {item.text}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    );
  }

  renderRadarSvg() {
  const centerX = 300;
  const centerY = 250;
  const maxRadius = 150;

  function getAngle(index) {
    return (Math.PI * 2 * index) / RADAR_AXES.length - Math.PI / 2;
  }

  function pointForAxis(index, value) {
    const angle = getAngle(index);
    const radius = (value / 100) * maxRadius;

    return {
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
    };
  }

  function labelPointForAxis(index) {
    const angle = getAngle(index);
    const radius = maxRadius + 42;

    return {
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
    };
  }

  function getTextAnchor(index) {
    const angle = getAngle(index);
    const cosValue = Math.cos(angle);

    if (cosValue > 0.25) return "start";
    if (cosValue < -0.25) return "end";
    return "middle";
  }

  function polygonPoints(phenotypeKey) {
    return RADAR_AXES
      .map(function(axis, index) {
        const point = pointForAxis(
          index,
          phenotypeData[phenotypeKey].radar[axis.key]
        );

        return point.x + "," + point.y;
      })
      .join(" ");
  }

  function renderPhenotypePolygon(phenotypeKey) {
    const phenotype = phenotypeData[phenotypeKey];

    return (
      <g key={phenotypeKey}>
        <polygon
          points={polygonPoints(phenotypeKey)}
          fill={phenotype.color}
          fillOpacity="0.16"
          stroke={phenotype.color}
          strokeWidth="3"
        />

        {RADAR_AXES.map(function(axis, index) {
          const point = pointForAxis(
            index,
            phenotype.radar[axis.key]
          );

          return (
            <circle
              key={axis.key}
              cx={point.x}
              cy={point.y}
              r="4"
              fill={phenotype.color}
              stroke="#ffffff"
              strokeWidth="1"
            />
          );
        })}
      </g>
    );
  }

  return (
    <svg width="100%" height="430" viewBox="0 0 600 500">
      <text
        x={centerX}
        y="34"
        textAnchor="middle"
        fontSize="24"
        fontWeight="800"
        fill="#1F2A44"
      >

      </text>

      {[0.25, 0.5, 0.75, 1].map(function(level) {
        const points = RADAR_AXES
          .map(function(axis, index) {
            const angle = getAngle(index);
            const radius = level * maxRadius;

            return (
              centerX +
              Math.cos(angle) * radius +
              "," +
              (centerY + Math.sin(angle) * radius)
            );
          })
          .join(" ");

        return (
          <polygon
            key={level}
            points={points}
            fill="none"
            stroke="#D9DEE8"
            strokeWidth="1.2"
          />
        );
      })}

      {[0.25, 0.5, 0.75, 1].map(function(level) {
        return (
          <text
            key={level}
            x={centerX - 12}
            y={centerY - level * maxRadius + 5}
            textAnchor="end"
            fontSize="13"
            fill="#7A869A"
          >
            {level.toFixed(2)}
          </text>
        );
      })}

      <text
        x={centerX - 8}
        y={centerY + 5}
        textAnchor="end"
        fontSize="13"
        fill="#7A869A"
      >
        0
      </text>

      {RADAR_AXES.map(function(axis, index) {
        const outer = pointForAxis(index, 100);
        const labelPoint = labelPointForAxis(index);

        return (
          <g key={axis.key}>
            <line
              x1={centerX}
              y1={centerY}
              x2={outer.x}
              y2={outer.y}
              stroke="#D9DEE8"
              strokeWidth="1.2"
            />

            <text
              x={labelPoint.x}
              y={labelPoint.y}
              textAnchor={getTextAnchor(index)}
              dominantBaseline="middle"
              fontSize="14"
              fontWeight="600"
              fill="#1F2A44"
            >
              {axis.label}
            </text>
          </g>
        );
      })}

      {renderPhenotypePolygon("molecular")}
      {renderPhenotypePolygon("immunogenic")}
      {renderPhenotypePolygon("metastatic")}
    </svg>
  );
}

  renderMatcher() {
    const self = this;
    const matcher = scorePattern(this.state.matcherFlags);
    const bestPhenotype = phenotypeData[matcher.best];

    return (
      <Card
        bordered={false}
        style={{
          marginTop: 24,
          borderRadius: 18,
          boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
        }}
      >
        <div
          style={{
            color: "#6B5BFF",
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: 13,
          }}
        >
          Educational pattern matcher
        </div>

        <div
          style={{
            fontSize: 30,
            fontWeight: 800,
            color: "#1F2A44",
            marginTop: 6,
          }}
        >
          Which PRIME-ICI pattern is closest?
        </div>

        <div
          style={{
            fontSize: 15,
            color: "#667085",
            marginTop: 8,
            marginBottom: 20,
          }}
        >
          This tool explains phenotype logic and is not intended for clinical
          decision-making.
        </div>

        <Alert
          message="Research communication only"
          description="This educational matcher is based on simplified phenotype-defining patterns. It is not a medical device and should not be used directly for clinical treatment decisions."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <Row gutter={20}>
          <Col span={13}>
            {matcherFeatures.map(function(feature) {
              const checked = !!self.state.matcherFlags[feature.key];

              return (
                <div
                  key={feature.key}
                  onClick={() => self.toggleMatcherFeature(feature.key)}
                  style={{
                    cursor: "pointer",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid " + (checked ? "#7D83FF" : "#EEF1F7"),
                    background: checked ? "#7D83FF10" : "#fff",
                    marginBottom: 9,
                    color: "#344054",
                  }}
                >
                  <Icon
                    type={checked ? "check-square" : "border"}
                    style={{
                      color: checked ? "#7D83FF" : "#98A2B3",
                      marginRight: 8,
                    }}
                  />
                  {feature.label}
                </div>
              );
            })}
          </Col>

          <Col span={11}>
            <div
              style={{
                borderRadius: 18,
                padding: 22,
                border: "2px solid " + bestPhenotype.color,
                background: bestPhenotype.color + "10",
                minHeight: 350,
              }}
            >
              <div
                style={{
                  color: "#667085",
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Closest PRIME-ICI pattern
              </div>

              <div
                style={{
                  fontSize: 30,
                  fontWeight: 800,
                  color: bestPhenotype.color,
                  marginTop: 8,
                }}
              >
                {bestPhenotype.name}
              </div>

              <div
                style={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  color: "#344054",
                  marginTop: 10,
                }}
              >
                {bestPhenotype.subtitle}
              </div>

              <Divider />

              {Object.keys(matcher.scores).map(function(key) {
                const phenotype = phenotypeData[key];
                const score = matcher.scores[key];
                const width = Math.min(score * 12, 100);

                return (
                  <div key={key} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ color: "#344054", fontWeight: 700 }}>
                        {phenotype.name}
                      </span>
                      <span style={{ color: "#667085" }}>{score}</span>
                    </div>

                    <div
                      style={{
                        height: 8,
                        background: "#EEF1F7",
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: 8,
                          width: width + "%",
                          background: phenotype.color,
                          borderRadius: 8,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      </Card>
    );
  }

  renderAbout() {
    return (
      <div
        style={{
          marginTop: 36,
          padding: "22px 0",
          color: "#667085",
          fontSize: 14,
        }}
      >
        <Icon type="info-circle" /> PRIME-ICI dashboard for research
        communication. This website is not a medical device and should not be
        used for clinical decision-making.
        <br></br><br></br>Please cite the following study if use the information in this website:
        <br></br><br></br>
                    <txt style={{ fontStyle: 'italic' }}>
                     Unsupervised Clinical–Molecular–Immune Phenotyping Defines Prognostic States in Non-Small Cell Lung Cancer Immunotherapy
                     </txt>
                    <br></br><br></br>
      </div>
    );
  }

  render() {
    return (
      <div>
        <Header1 />

        <Content style={{ padding: "0 50px" }}>
          <Layout style={{ background: "#fff" }}>
            <Sider
              width={220}
              style={{
                background: "#fff",
                position: "sticky",
                top: 0,
                alignSelf: "flex-start",
                height: "100vh",
                overflow: "auto",
              }}
            >
              <Menu
                mode="inline"
                selectedKeys={[this.state.selectedMenu]}
                defaultOpenKeys={["sub1"]}
                onClick={this.handleMenuClick}
                style={{ height: "100%" }}
              >
                <SubMenu
                  key="sub1"
                  title={
                    <span>
                      <Icon type="radar-chart" />
                      PRIME-ICI
                    </span>
                  }
                >
                  <Menu.Item key="introduction">Introduction</Menu.Item>
                  <Menu.Item key="phenotypes">Phenotypes</Menu.Item>
                  <Menu.Item key="validation">Validation</Menu.Item>
                  <Menu.Item key="matcher">Matcher</Menu.Item>
                  <Menu.Item key="about">About PRIME-ICI</Menu.Item>
                </SubMenu>
              </Menu>
            </Sider>

            <Content style={{ padding: "0 50px 60px" }}>
              <div id="prime-introduction">
                {this.renderHero()}
                {this.renderSummaryCards()}
                {this.renderAxisCards()}
              </div>

              <div id="prime-phenotypes">
                <Row gutter={24} style={{ marginTop: 24 }}>
                  <Col span={10}>
                    <Card
                      bordered={false}
                      style={{
                        borderRadius: 18,
                        boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
                        minHeight: 570,
                      }}
                    >
                      <div
                        style={{
                          color: "#6B5BFF",
                          fontWeight: 800,
                          letterSpacing: 1,
                          textTransform: "uppercase",
                          fontSize: 13,
                          marginBottom: 8,
                        }}
                      >
                        Phenotype explorer
                      </div>

                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: "#1F2A44",
                          marginBottom: 18,
                        }}
                      >
                        Select a PRIME phenotype
                      </div>

                      {this.renderPhenotypeSelector()}
                    </Card>
                  </Col>

                  <Col span={14}>
                    <Card
                      bordered={false}
                      style={{
                        borderRadius: 18,
                        boxShadow: "0 12px 32px rgba(25,35,80,0.08)",
                        minHeight: 570,
                      }}
                    >
                      {this.renderPhenotypeDetail()}

                      <Divider />

                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#1F2A44",
                          marginBottom: 16,
                        }}
                      >
                        Top NMF basis-weight drivers
                      </div>

                      {this.renderDriverBars()}
                    </Card>
                  </Col>
                </Row>
              </div>

              <div id="prime-validation">
  <Row
    gutter={24}
    style={{
      marginTop: 24,
      display: "flex",
    }}
  >
    <Col
      span={12}
      style={{
        display: "flex",
      }}
    >
      <Card
        bordered={false}
        style={{
          ...validationCardStyle,
          width: "100%",
        }}
      >
        <div style={validationSectionTitleStyle}>
          External validation
        </div>

        <div
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: "#1F2A44",
            marginBottom: 28,
            lineHeight: 1.25,
          }}
        >
          Phenotype prevalence across cohorts
        </div>

        {this.renderDistributionBars()}
      </Card>
    </Col>

    <Col
      span={12}
      style={{
        display: "flex",
      }}
    >
      <Card
        bordered={false}
        style={{
          ...validationCardStyle,
          width: "100%",
        }}
      >
        <div style={validationSectionTitleStyle}>
          Feature architecture
        </div>

        <div
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: "#1F2A44",
            marginBottom: 16,
            lineHeight: 1.25,
          }}
        >
          Top characteristics
        </div>

        {this.renderRadarSvg()}
      </Card>
    </Col>
  </Row>

  {this.renderSharedPhenotypeLegend()}
  {this.renderValidationStatistics()}
  {this.renderContinuum()}
</div>

              <div id="prime-matcher">
                {this.renderMatcher()}
              </div>

              <div id="prime-about">
                {this.renderAbout()}
              </div>
            </Content>
          </Layout>
        </Content>
      </div>
    );
  }
}

export default PRIMEICI;